import { useMutation } from '@tanstack/react-query';
import download from 'downloadjs';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { endpoints } from '@rossum/api-client';
import { absoluteApiUrl } from '../../../constants/config';
import { api } from '../../../lib/apiClient';
import { getFilenameForExport } from '../../../redux/modules/annotations/epics';
import { safeOrganizationSelector } from '../../../redux/modules/organization/selectors';
import { ExportColumnsType } from '../../../types/organization';
import { DownloadFormat } from '../export-menu/formats';

const organizationUISettingsExportSchema = z
  .object({
    columns: z.array(z.string()).optional(),
    prepend_columns: z.array(z.string()).default(['meta_file_name']),
    append_columns: z
      .array(z.string())
      .default(['meta_arrived_at', 'meta_url', 'meta_file']),
  })
  .default({});

export const useExportAnnotationsCrossQueue = () => {
  const organizationUISettingsExport = useSelector(safeOrganizationSelector)
    ?.uiSettings?.export;

  return useMutation({
    mutationFn: ({
      format,
      selectedAnnotationsIds,
      toExport,
    }: {
      toExport?: boolean;
      queueName: string | null;
      format?: DownloadFormat;
      selectedAnnotationsIds?: number[];
    }) => {
      const annotations = selectedAnnotationsIds?.map(
        id => `${absoluteApiUrl}/annotations/${id}`
      );
      const { prepend_columns, append_columns, columns }: ExportColumnsType =
        organizationUISettingsExportSchema.parse(organizationUISettingsExport);
      const pageSize = selectedAnnotationsIds?.length;

      return api.request(
        endpoints.annotations.exportAnnotationCrossQueue(
          {
            format: format ?? 'json',
            prependColumns: prepend_columns.toString(),
            appendColumns: append_columns.toString(),
            columns: columns?.toString(),
            pageSize,
            ...(toExport ? { toStatus: 'exporting' } : {}),
          },
          {
            annotations,
          }
        )
      );
    },

    // No `format` means export-only — skip the file download.
    onSuccess: (data: Blob, params) => {
      if (!params.format) return;

      const { type } = data;

      download(
        data,
        getFilenameForExport(null, params.queueName, params.format),
        type
      );
    },
  });
};
